import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Edit, Trash2, Eye, Percent, ChevronLeft, ChevronRight, CalendarDays, List, Lock, Plus, FileText, Send, KeyRound, RefreshCw, Loader2, Mail } from "lucide-react";
import {
  startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths, subMonths,
  parseISO, isWithinInterval, isSameDay, isSameMonth, startOfWeek, endOfWeek, addDays,
} from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [discountAmount, setDiscountAmount] = useState("");

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [selectingRange, setSelectingRange] = useState({ start: null, end: null });

  // Send document state
  const [sendDocDialogOpen, setSendDocDialogOpen] = useState(false);
  const [sendDocBooking, setSendDocBooking] = useState(null);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [sendingDoc, setSendingDoc] = useState(false);
  const [manualDoorCode, setManualDoorCode] = useState("");

  // Door code state
  const [doorLoading, setDoorLoading] = useState(false);

  // Send email state
  const [sendingEmailId, setSendingEmailId] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchBlockedDates();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, statusFilter, searchTerm]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    if (statusFilter !== "all") {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (b) =>
          b.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.guest_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: newStatus });
      toast.success(`Bókun ${newStatus === 'confirmed' ? 'staðfest' : 'afbókuð'}`);
      fetchBookings();
    } catch (error) {
      toast.error("Villa við að uppfæra bókun");
    }
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditForm({
      guest_name: booking.guest_name,
      guest_email: booking.guest_email,
      guest_phone: booking.guest_phone,
      check_in: booking.check_in,
      check_out: booking.check_out,
      num_guests: booking.num_guests,
      special_requests: booking.special_requests || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/bookings/${selectedBooking.id}`, editForm);
      toast.success("Bókun uppfærð!");
      setEditDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error("Villa við að uppfæra bókun");
    }
  };

  const handleApplyDiscount = (booking) => {
    setSelectedBooking(booking);
    setDiscountAmount("");
    setDiscountDialogOpen(true);
  };

  const handleSaveDiscount = async () => {
    const discount = parseFloat(discountAmount);
    if (isNaN(discount) || discount < 0) {
      toast.error("Ógildur afsláttur");
      return;
    }

    const newTotal = selectedBooking.total_price - discount;
    if (newTotal < 0) {
      toast.error("Afsláttur getur ekki verið meira en heildarverð");
      return;
    }

    try {
      await axios.put(`${API}/bookings/${selectedBooking.id}`, {
        discount: discount,
        total_price: newTotal,
      });
      toast.success(`Afsláttur €${discount} bætt við!`);
      setDiscountDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error("Villa við að bæta við afslætti");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Ertu viss um að þú viljir eyða þessari bókun?")) {
      return;
    }

    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success("Bókun eytt");
      fetchBookings();
    } catch (error) {
      toast.error("Villa við að eyða bókun");
    }
  };

  // Blocked dates
  const fetchBlockedDates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlockedDates(response.data);
    } catch (error) {
      console.error("Error fetching blocked dates:", error);
    }
  };

  const handleBlockDates = async () => {
    if (!blockForm.start_date || !blockForm.end_date) {
      toast.error("Veldu upphafs- og lokadagsetningu");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/blocked-dates`, blockForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Dagar lokaðir!");
      setBlockDialogOpen(false);
      setBlockForm({ start_date: "", end_date: "", reason: "" });
      setSelectingRange({ start: null, end: null });
      fetchBlockedDates();
    } catch (error) {
      toast.error("Villa við að loka dögum");
    }
  };

  const handleDeleteBlocked = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/blocked-dates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lokun fjarlægð");
      fetchBlockedDates();
    } catch (error) {
      toast.error("Villa við að fjarlægja lokun");
    }
  };

  // Send document functions
  const openSendDocDialog = async (booking) => {
    setSendDocBooking(booking);
    setSelectedDocId("");
    setManualDoorCode(booking.door_code || "");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableDocs(res.data);
    } catch { setAvailableDocs([]); }
    setSendDocDialogOpen(true);
  };

  const handleSendDoc = async () => {
    if (!selectedDocId || !sendDocBooking) return;
    setSendingDoc(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/documents/send`,
        { document_id: selectedDocId, booking_id: sendDocBooking.id, door_code: manualDoorCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSendDocDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að senda skjal");
    } finally {
      setSendingDoc(false);
    }
  };

  // Door code functions
  const handleDoorCode = async (bookingId, action) => {
    setDoorLoading(true);
    const token = localStorage.getItem("token");
    try {
      if (action === "delete") {
        await axios.delete(`${API}/bookings/${bookingId}/door-code`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Dyraaðgangskóði fjarlægður");
      } else {
        const url = action === "regenerate"
          ? `${API}/bookings/${bookingId}/door-code/regenerate`
          : `${API}/bookings/${bookingId}/door-code/generate`;
        const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Kóði: ${res.data.door_code}`);
      }
      fetchBookings();
      // Re-fetch selected booking for view dialog
      if (selectedBooking?.id === bookingId) {
        const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
        const updated = res.data.find(b => b.id === bookingId);
        if (updated) setSelectedBooking(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa");
    } finally {
      setDoorLoading(false);
    }
  };

  // Send confirmation + payment email
  const handleSendConfirmation = async (booking) => {
    setSendingEmailId(booking.id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/bookings/${booking.id}/send-confirmation`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að senda tölvupóst");
    } finally {
      setSendingEmailId(null);
    }
  };

  // Calendar helpers
  const getCalendarDays = (month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  };

  const getBookingsForDay = (day) => {
    return bookings.filter((b) => {
      if (b.status === "cancelled") return false;
      const checkIn = parseISO(b.check_in);
      const checkOut = addDays(parseISO(b.check_out), -1);
      return isWithinInterval(day, { start: checkIn, end: checkOut }) || isSameDay(day, checkIn) || isSameDay(day, checkOut);
    });
  };

  const isBlockedDay = (day) => {
    return blockedDates.some((b) => {
      const start = parseISO(b.start_date);
      const end = parseISO(b.end_date);
      return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end);
    });
  };

  const getBlockedForDay = (day) => {
    return blockedDates.find((b) => {
      const start = parseISO(b.start_date);
      const end = parseISO(b.end_date);
      return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end);
    });
  };

  const handleCalendarDayClick = (day) => {
    if (!selectingRange.start) {
      setSelectingRange({ start: day, end: null });
    } else if (!selectingRange.end) {
      const start = selectingRange.start;
      const ordered = day >= start ? { start, end: day } : { start: day, end: start };
      setSelectingRange(ordered);
      setBlockForm({
        ...blockForm,
        start_date: format(ordered.start, "yyyy-MM-dd"),
        end_date: format(ordered.end, "yyyy-MM-dd"),
      });
      setBlockDialogOpen(true);
    } else {
      setSelectingRange({ start: day, end: null });
    }
  };

  const isInSelectRange = (day) => {
    if (!selectingRange.start) return false;
    if (!selectingRange.end) return isSameDay(day, selectingRange.start);
    return isWithinInterval(day, { start: selectingRange.start, end: selectingRange.end });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-green-100 text-green-800 border-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      <Badge className={variants[status] || ""} data-testid={`badge-${status}`}>
        {status === 'pending' ? 'Í bið' : status === 'confirmed' ? 'Staðfest' : 'Afbókuð'}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight mb-8" data-testid="bookings-title">
          Bókunastjórnun
        </h1>

        <Tabs defaultValue="list" className="w-full">
          <TabsList className="mb-6" data-testid="bookings-tabs">
            <TabsTrigger value="list" className="gap-2" data-testid="tab-list">
              <List className="w-4 h-4" /> Tafla
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2" data-testid="tab-calendar">
              <CalendarDays className="w-4 h-4" /> Dagatal
            </TabsTrigger>
          </TabsList>

          {/* LIST VIEW */}
          <TabsContent value="list">
        {/* Filters */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Leita að gesti
              </label>
              <Input
                placeholder="Leita að nafni eða netfangi"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-3 rounded-lg"
                data-testid="search-input"
              />
            </div>
            <div>
              <label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Sía eftir stöðu
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Allar bókanir</SelectItem>
                  <SelectItem value="pending">Í bið</SelectItem>
                  <SelectItem value="confirmed">Staðfest</SelectItem>
                  <SelectItem value="cancelled">Afbókuð</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {filteredBookings.length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-8">Engar bókanir fundust</p>
          ) : (
            <div className="overflow-x-auto" data-testid="bookings-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                      Gestur
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                      Dagsetningar
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                      Verð
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                      Staða
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                      Aðgerðir
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`booking-card-${booking.id}`}>
                      <td className="py-4 px-4">
                        <div className="font-body font-semibold">{booking.guest_name}</div>
                        <div className="text-sm text-muted-foreground">{booking.num_guests} gestir</div>
                      </td>
                      <td className="py-4 px-4 font-body text-sm">
                        <div>{booking.check_in}</div>
                        <div>{booking.check_out}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-body font-semibold text-primary-600">€{booking.total_price.toFixed(0)}</div>
                        {booking.discount && booking.discount > 0 && (
                          <div className="text-xs text-green-600">Afsláttur: €{booking.discount}</div>
                        )}
                      </td>
                      <td className="py-4 px-4">{getStatusBadge(booking.status)}</td>
                      <td className="py-4 px-4">
                        <div className="flex gap-1 flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewBooking(booking)}
                            className="text-secondary-600 hover:bg-secondary-50"
                            data-testid={`view-booking-${booking.id}`}
                            title="Opna"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditBooking(booking)}
                            className="text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-booking-${booking.id}`}
                            title="Breyta"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApplyDiscount(booking)}
                            className="text-green-600 hover:bg-green-50"
                            data-testid={`discount-booking-${booking.id}`}
                            title="Afsláttur"
                          >
                            <Percent className="w-4 h-4" />
                          </Button>
                          {booking.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => updateBookingStatus(booking.id, "confirmed")}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`confirm-booking-${booking.id}`}
                                title="Staðfesta"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateBookingStatus(booking.id, "cancelled")}
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                data-testid={`cancel-booking-${booking.id}`}
                                title="Afbóka"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-booking-${booking.id}`}
                            title="Eyða"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openSendDocDialog(booking)}
                            className="text-violet-600 hover:bg-violet-50"
                            data-testid={`send-doc-${booking.id}`}
                            title="Senda skjal"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSendConfirmation(booking)}
                            disabled={sendingEmailId === booking.id}
                            className="text-blue-600 hover:bg-blue-50"
                            data-testid={`send-email-${booking.id}`}
                            title="Senda staðfestingu/greiðsluupplýsingar"
                          >
                            {sendingEmailId === booking.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </TabsContent>

          {/* CALENDAR VIEW */}
          <TabsContent value="calendar">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button variant="outline" size="sm" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))} data-testid="cal-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-xl font-heading font-semibold capitalize" data-testid="cal-month-title">
                  {format(calendarMonth, "MMMM yyyy")}
                </h2>
                <Button variant="outline" size="sm" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))} data-testid="cal-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-green-500"></div> Staðfest</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-yellow-400"></div> Í bið</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-400"></div> Lokað</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-300"></div> Valið</div>
                <p className="text-muted-foreground ml-auto text-xs">Smelltu á tvo daga til að loka tímabili</p>
              </div>

              {/* Week headers */}
              <div className="grid grid-cols-7 gap-px mb-1">
                {["Mán","Þri","Mið","Fim","Fös","Lau","Sun"].map(d => (
                  <div key={d} className="text-center text-xs font-ui font-medium text-muted-foreground py-2">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-px bg-neutral-100 border border-neutral-200 rounded-lg overflow-hidden">
                {getCalendarDays(calendarMonth).map((day, i) => {
                  const dayBookings = getBookingsForDay(day);
                  const blocked = isBlockedDay(day);
                  const blockedEntry = getBlockedForDay(day);
                  const inMonth = isSameMonth(day, calendarMonth);
                  const isToday = isSameDay(day, new Date());
                  const inRange = isInSelectRange(day);

                  return (
                    <div
                      key={i}
                      className={`min-h-[80px] md:min-h-[100px] p-1 bg-white cursor-pointer transition-colors hover:bg-neutral-50 ${!inMonth ? "opacity-40" : ""} ${isToday ? "ring-2 ring-inset ring-primary/40" : ""} ${inRange ? "!bg-blue-50" : ""}`}
                      onClick={() => handleCalendarDayClick(day)}
                      data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <div className={`text-xs font-ui mb-1 ${isToday ? "font-bold text-primary" : "text-neutral-500"}`}>
                        {format(day, "d")}
                      </div>
                      {blocked && (
                        <div
                          className="text-[10px] leading-tight bg-red-100 text-red-700 rounded px-1 py-0.5 mb-0.5 flex items-center gap-0.5 group relative"
                          title={blockedEntry?.reason || "Lokað"}
                        >
                          <Lock className="w-2.5 h-2.5 shrink-0" />
                          <span className="truncate">{blockedEntry?.reason || "Lokað"}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteBlocked(blockedEntry.id); }}
                            className="ml-auto opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 shrink-0"
                            title="Fjarlægja lokun"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      {dayBookings.slice(0, 2).map((b) => (
                        <div
                          key={b.id}
                          className={`text-[10px] leading-tight rounded px-1 py-0.5 mb-0.5 truncate cursor-pointer ${
                            b.status === "confirmed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          }`}
                          onClick={(e) => { e.stopPropagation(); handleViewBooking(b); }}
                          title={`${b.guest_name} (${b.check_in} - ${b.check_out})`}
                        >
                          {b.guest_name}
                        </div>
                      ))}
                      {dayBookings.length > 2 && (
                        <div className="text-[9px] text-muted-foreground">+{dayBookings.length - 2} fleiri</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Blocked dates list */}
              {blockedDates.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Lokaðir dagar
                  </h3>
                  <div className="space-y-2">
                    {blockedDates.map((b) => (
                      <div key={b.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-lg px-4 py-2" data-testid={`blocked-${b.id}`}>
                        <div>
                          <span className="font-ui text-sm font-medium">{b.start_date} &rarr; {b.end_date}</span>
                          {b.reason && <span className="text-sm text-muted-foreground ml-2">({b.reason})</span>}
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-100" onClick={() => handleDeleteBlocked(b.id)} data-testid={`delete-blocked-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Block Dates Dialog */}
        <Dialog open={blockDialogOpen} onOpenChange={(open) => { setBlockDialogOpen(open); if (!open) setSelectingRange({ start: null, end: null }); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Loka dögum</DialogTitle>
              <DialogDescription>Veldu tímabil sem á að loka fyrir bókanir</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frá</Label>
                  <Input type="date" value={blockForm.start_date} onChange={(e) => setBlockForm({ ...blockForm, start_date: e.target.value })} data-testid="block-start-date" />
                </div>
                <div>
                  <Label>Til</Label>
                  <Input type="date" value={blockForm.end_date} onChange={(e) => setBlockForm({ ...blockForm, end_date: e.target.value })} data-testid="block-end-date" />
                </div>
              </div>
              <div>
                <Label>Ástæða (valfrjálst)</Label>
                <Input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} placeholder="t.d. Viðhald, Eigin notkun..." data-testid="block-reason" />
              </div>
              <Button onClick={handleBlockDates} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-block-dates">
                <Lock className="w-4 h-4 mr-2" /> Loka dögum
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Bókunarupplýsingar</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Gestur</Label>
                    <p className="font-semibold">{selectedBooking.guest_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Netfang</Label>
                    <p className="font-body">{selectedBooking.guest_email}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Sími</Label>
                    <p className="font-body">{selectedBooking.guest_phone}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Fjöldi gesta</Label>
                    <p className="font-body">{selectedBooking.num_guests}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Inn</Label>
                    <p className="font-body">{selectedBooking.check_in}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Út</Label>
                    <p className="font-body">{selectedBooking.check_out}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Heildarverð</Label>
                    <p className="font-bold text-primary-600 text-lg">€{selectedBooking.total_price.toFixed(0)}</p>
                  </div>
                  {selectedBooking.discount && selectedBooking.discount > 0 && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Afsláttur</Label>
                      <p className="font-semibold text-green-600">€{selectedBooking.discount.toFixed(0)}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label className="text-sm text-muted-foreground">Staða</Label>
                    <div className="mt-1">{getStatusBadge(selectedBooking.status)}</div>
                  </div>
                </div>
                {selectedBooking.special_requests && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Sérstakar óskir</Label>
                    <p className="font-body mt-1 p-3 bg-neutral-50 rounded-lg">{selectedBooking.special_requests}</p>
                  </div>
                )}

                {/* Door Access Card */}
                <div className="border border-neutral-200 rounded-lg p-4 mt-2" data-testid="door-access-card">
                  <div className="flex items-center gap-2 mb-3">
                    <KeyRound className="w-5 h-5 text-primary" />
                    <Label className="text-sm font-semibold">Dyraaðgangur</Label>
                  </div>
                  {selectedBooking.door_code_status === "active" && selectedBooking.door_code ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-2xl font-bold tracking-widest bg-neutral-100 px-4 py-2 rounded-lg" data-testid="door-code-display">
                          {selectedBooking.door_code}
                        </span>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Virkur</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Gildir: {selectedBooking.door_code_valid_from} &rarr; {selectedBooking.door_code_valid_to}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" disabled={doorLoading} onClick={() => handleDoorCode(selectedBooking.id, "regenerate")} data-testid="door-regenerate-btn">
                          {doorLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Endurnýja
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={doorLoading} onClick={() => handleDoorCode(selectedBooking.id, "delete")} data-testid="door-delete-btn">
                          <Trash2 className="w-3 h-3 mr-1" /> Eyða
                        </Button>
                      </div>
                    </div>
                  ) : selectedBooking.door_code_status === "error" ? (
                    <div className="space-y-2">
                      <p className="text-sm text-red-600">Villa: {selectedBooking.door_code_last_error || "Óþekkt villa"}</p>
                      <Button size="sm" disabled={doorLoading} onClick={() => handleDoorCode(selectedBooking.id, "regenerate")} data-testid="door-retry-btn">
                        {doorLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Reyna aftur
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Enginn aðgangskóði búinn til</p>
                      <Button size="sm" disabled={doorLoading} onClick={() => handleDoorCode(selectedBooking.id, "generate")} data-testid="door-generate-btn">
                        {doorLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Búa til kóða
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Booking Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Breyta bókun</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nafn gests</Label>
                    <Input
                      value={editForm.guest_name}
                      onChange={(e) => setEditForm({...editForm, guest_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Netfang</Label>
                    <Input
                      type="email"
                      value={editForm.guest_email}
                      onChange={(e) => setEditForm({...editForm, guest_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Sími</Label>
                    <Input
                      value={editForm.guest_phone}
                      onChange={(e) => setEditForm({...editForm, guest_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Fjöldi gesta</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editForm.num_guests}
                      onChange={(e) => setEditForm({...editForm, num_guests: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Innritun</Label>
                    <Input
                      type="date"
                      value={editForm.check_in}
                      onChange={(e) => setEditForm({...editForm, check_in: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Útritun</Label>
                    <Input
                      type="date"
                      value={editForm.check_out}
                      onChange={(e) => setEditForm({...editForm, check_out: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Sérstakar óskir</Label>
                  <Textarea
                    value={editForm.special_requests}
                    onChange={(e) => setEditForm({...editForm, special_requests: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
                <Button
                  onClick={handleSaveEdit}
                  className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white"
                >
                  Vista breytingar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Discount Dialog */}
        <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gefa afslátt</DialogTitle>
              <DialogDescription>
                Bættu við afslætti fyrir þessa bókun
              </DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4">
                <div className="p-4 bg-neutral-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Upprunalegt verð:</span>
                    <span className="font-semibold">€{selectedBooking.total_price.toFixed(0)}</span>
                  </div>
                  {selectedBooking.discount && selectedBooking.discount > 0 && (
                    <div className="flex justify-between mb-2 text-green-600">
                      <span className="text-sm">Núverandi afsláttur:</span>
                      <span className="font-semibold">-€{selectedBooking.discount.toFixed(0)}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Afsláttur (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="Sláðu inn upphæð"
                    data-testid="discount-input"
                  />
                </div>
                {discountAmount && (
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex justify-between text-lg font-bold text-primary-700">
                      <span>Nýtt heildarverð:</span>
                      <span>€{(selectedBooking.total_price - parseFloat(discountAmount || 0)).toFixed(0)}</span>
                    </div>
                  </div>
                )}
                <Button
                  onClick={handleSaveDiscount}
                  className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white"
                  data-testid="save-discount-button"
                >
                  Bæta við afslætti
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Document Dialog */}
        <Dialog open={sendDocDialogOpen} onOpenChange={setSendDocDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Senda skjal á gest</DialogTitle>
              <DialogDescription>
                {sendDocBooking && `Senda á: ${sendDocBooking.guest_name} (${sendDocBooking.guest_email})`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {availableDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Engin skjöl. Farðu í Skjöl til að hlaða upp.
                </p>
              ) : (
                <>
                  <div>
                    <Label className="mb-2 block">Veldu skjal</Label>
                    <Select value={selectedDocId} onValueChange={setSelectedDocId}>
                      <SelectTrigger data-testid="select-document">
                        <SelectValue placeholder="Veldu skjal..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDocs.map((doc) => (
                          <SelectItem key={doc.id} value={doc.id}>{doc.original_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Door Code */}
                  <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                    <div className="flex items-center gap-2 mb-2">
                      <KeyRound className="w-4 h-4 text-primary" />
                      <Label className="text-sm font-semibold">Dyraaðgangskóði (valfrjálst)</Label>
                    </div>
                    <Input
                      value={manualDoorCode}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setManualDoorCode(v);
                      }}
                      placeholder="6 tölustafir"
                      maxLength={6}
                      className="font-mono text-lg tracking-widest text-center px-4 py-2"
                      data-testid="manual-door-code-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Ef fyllt út sendist kóðinn með í tölvupóstinum og vistast á bókuninni
                    </p>
                  </div>

                  <Button
                    onClick={handleSendDoc}
                    disabled={!selectedDocId || sendingDoc}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="confirm-send-doc"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingDoc ? "Sendi..." : "Senda skjal"}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BookingsManagement;
